import DOMPurify from "dompurify";

/**
 * Sanitizer for diff HTML that originates from a monitored page.
 *
 * The backend's `apply_html_color_to_body` only substitutes its own
 * placemarkers for <span> tags — it never escapes the page text around them.
 * So whatever the monitored site rendered arrives here verbatim, and a page
 * that displays escaped markup (a forum, a paste site, a text/plain watch) can
 * carry `<img src=x onerror=...>` straight into the authenticated dashboard
 * origin. Share links make that reachable: "anyone with this link can add this
 * watch to their account".
 *
 * The allowlist mirrors exactly what the backend emits and nothing else.
 */
const ALLOWED_TAGS = ["span", "br"];
const ALLOWED_ATTR = ["style", "role", "aria-label", "title"];

export function sanitizeDiffHtml(html: string | undefined | null): string {
  if (!html) return "";

  // DOMPurify needs a DOM. The diff card only renders after a chat tool call,
  // so this never runs during SSR — but fall back to escaped text rather than
  // trusting the input if it somehow does.
  if (typeof window === "undefined") return escapeHtml(html);

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Never allow a sanitized fragment to reach back into the page.
    FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["srcset", "action", "formaction"],
    ALLOW_DATA_ATTR: false,
  });
}

/** Escape text for safe interpolation into HTML. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Serialize an object for embedding in a <script type="application/ld+json">.
 *
 * Escaping the literal "</script>" is not enough: the HTML tokenizer ends a
 * script element at "</script" followed by whitespace, "/" or ">", so a watch
 * title of `</script ><img src=x onerror=...>` broke out of the JSON-LD block
 * and executed on every visitor to the community pages.
 *
 * Escaping "<" itself as \u003c is inert in HTML and still valid JSON, so the
 * structured data parses exactly as before.
 */
export function jsonLdScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}
